const MAP_DATA_URL = '/api/map-data.json';
const NL_CATEGORIES_URL = '/api/nl-categories-map.json';
const PREVIEW_BOX_WIDTH = 120;
const PREVIEW_FALLBACK_ASPECT = '1';

function normalizePath(href) {
  if (!href || typeof href !== 'string') return '';
  const t = href.trim();
  if (!t) return '';

  try {
    const u = new URL(t, window.location.origin);
    let p = u.pathname.replace(/\/+$/, '') || '/';
    return p;
  } catch {
    let p = t.startsWith('/') ? t : '/' + t;
    p = p.replace(/\/+$/, '') || '/';
    return p;
  }
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function setPreviewKey(store, key, meta) {
  if (!key || !meta?.imageUrl || store[key]) return;
  store[key] = meta;
}

function buildPreviewLookup(rows) {  
  const store = {};
  for (const row of rows || []) {
    const imageUrl = row.previewImage;
    if (!imageUrl) continue;

    const w = row.previewWidth;
    const h = row.previewHeight;
    const meta = {
      imageUrl,
      width: typeof w === 'number' && w > 0 ? w : null,
      height: typeof h === 'number' && h > 0 ? h : null,
    };

    setPreviewKey(store, row.path, meta);
    setPreviewKey(store, row.pathRelative, meta);
    setPreviewKey(store, row.url, meta);

    if (row.url) {
      setPreviewKey(store, normalizePath(row.url), meta);
    }
    if (row.path) {
      setPreviewKey(store, normalizePath(row.path), meta);
    }
  }
  return store;
}

function metaForHotlink(store, hotlink) {
  if (!hotlink) return null;
  const raw = String(hotlink).trim();
  if (store[raw]) return store[raw];

  const normalized = normalizePath(raw);
  if (normalized && store[normalized]) return store[normalized];

  const relative = normalized.replace(/^\//, '');
  if (relative && store[relative]) return store[relative];

  return null;
}

const initMap = async function () {
  const mapEl = document.getElementById('map');
  if (!mapEl) return;

  const zoom = parseFloat(mapEl.dataset.zoom) || 6;

  const map = new google.maps.Map(mapEl, {
    center: {
      lat: 52.7917636,
      lng: -2.858130200000005,
    },
    mapId: 'e547d98859c937b2',
    zoom,
    scrollwheel: false,
    streetViewControl: false,
  });

  map.data.setStyle({
    fillColor: '#FF880A',
    fillOpacity: 0.5,
    strokeWeight: 1,
    strokeColor: '#FF880A',
  });

  class MapPopup extends google.maps.OverlayView {
    constructor() {
      super();
      this.visible = false;
      this.position = new google.maps.LatLng(0, 0);

      const content = document.createElement('div');
      content.classList.add('popup');

      const bubbleAnchor = document.createElement('div');
      bubbleAnchor.classList.add('popup__anchor');
      bubbleAnchor.appendChild(content);

      this.containerDiv = document.createElement('div');
      this.containerDiv.classList.add('popup__container');
      this.containerDiv.appendChild(bubbleAnchor);
      this.containerDiv.style.pointerEvents = 'none';

      this.contentEl = content;
    }

    show(latLng, html) {
      this.position = latLng;
      this.contentEl.innerHTML = html;
      this.visible = true;
      this.draw();
    }

    hide() {
      this.visible = false;
      this.draw();
    }

    onAdd() {
      this.getPanes().floatPane.appendChild(this.containerDiv);
    }

    onRemove() {
      if (this.containerDiv.parentElement) {
        this.containerDiv.parentElement.removeChild(this.containerDiv);
      }
    }

    draw() {
      if (!this.visible) {
        this.containerDiv.style.display = 'none';
        return;
      }

      const projection = this.getProjection();
      if (!projection) return;

      const divPosition = projection.fromLatLngToDivPixel(this.position);

      const display =
        Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
          ? 'block'
          : 'none';

      if (display === 'block') {
        this.containerDiv.style.left = divPosition.x + 'px';
        this.containerDiv.style.top = divPosition.y + 'px';
      }

      if (this.containerDiv.style.display !== display) {
        this.containerDiv.style.display = display;
      }
    }
  }

  const mapPopup = new MapPopup();
  mapPopup.setMap(map);

  const [mapRes, catRes] = await Promise.all([
    fetch(MAP_DATA_URL),
    fetch(NL_CATEGORIES_URL),
  ]);

  if (!mapRes.ok) {
    throw new Error('map-data: ' + mapRes.status);
  }
  if (!catRes.ok) {
    throw new Error('nl-categories: ' + catRes.status);
  }

  const mapPayload = await mapRes.json();
  const catPayload = await catRes.json();

  for (const row of mapPayload.data || []) {
    if (row.url) {
      map.data.loadGeoJson(row.url);
    }
  }

  const previewByHotlink = buildPreviewLookup(catPayload.data);

  map.data.addListener('mouseover', function (event) {
    const name = event.feature.getProperty('name');
    const hotlink = event.feature.getProperty('hotlink');
    const meta = metaForHotlink(previewByHotlink, hotlink);

    let html =
      '<div style="width:' +
      PREVIEW_BOX_WIDTH +
      'px;display:flex;flex-direction:column;align-items:center;max-width:100%;margin:0 auto;text-align:center;box-sizing:border-box;overflow:hidden;">';

    if (meta?.imageUrl) {
      const w = meta.width;
      const h = meta.height;
      const aspectCss =
        w && h ? `${w} / ${h}` : PREVIEW_FALLBACK_ASPECT;
      const imgH =
        w && h
          ? Math.max(1, Math.round((PREVIEW_BOX_WIDTH * h) / w))
          : Math.round(PREVIEW_BOX_WIDTH);

      html +=
        '<div style="width:100%;aspect-ratio:' +
        aspectCss +
        ';margin:0 auto 10px;">' +
        '<img src="' +
        meta.imageUrl +
        '" alt="" width="' +
        PREVIEW_BOX_WIDTH +
        '" height="' +
        imgH +
        '" decoding="async" fetchpriority="low" ' +
        'style="width:100%;height:100%;object-fit:cover;display:block;border-radius:4px;">' +
        '</div>';
    }

    html += escapeHtml(name) + '</div>';

    mapPopup.show(event.latLng, html);
    map.data.overrideStyle(event.feature, { fillOpacity: 0.8 });
  });

  map.data.addListener('mouseout', function () {
    mapPopup.hide();
    map.data.revertStyle();
  });

  map.data.addListener('click', function (event) {
    const hotlink = event.feature.getProperty('hotlink');
    if (hotlink) {
      document.location.href = hotlink;
    }
  });
};

function checkForMapScript() {
  const mapParent = document.getElementById('listings-map');
  if (!mapParent) return;
  window.google = null;
  const newScript = document.createElement('script');
  newScript.async = true;
  newScript.src =
  'https://maps.googleapis.com/maps/api/js?key=AIzaSyBOu0xJB2Dmae9R5bW2DfSyIoIIV34Iopg&callback=initMap&loading=async&v=weekly';
  mapParent.append(newScript);
}

window.initMap = initMap;

const map = {
  checkForMapScript: checkForMapScript,
};

export default map;